import React, { useState, useEffect } from 'react'

import { Tooltip } from '@mui/material'
import { t } from 'i18next'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import dayjs from 'dayjs'

import AppLayout from '../../components/Layouts/AppLayout'
import AddButton from '../../components/AddButton'
import Loading from '../../components/Loading'
import TableShared from '../../components/TableShared'
import { useStateContext } from '../../context/ContextProvider'
import axios from '../../lib/axios'
import { useAuth } from '../../hooks/auth'
import { isValid } from '../../helpers/helper'
import { buildUrl } from '../../helpers/apiHelper'

const QuotePricingTiers = () => {

    const { user } = useAuth({ middleware: 'guest' })
    const navigate = useNavigate()

    const { company_id, choosesite, setChoosesite, config, pusher } = useStateContext()

    const [tiers, setTiers] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [random, setRandom] = useState(null)

    /* site follows the global site chosen in the navigation, same as Quotes/Routings */
    useEffect(() => {
        setChoosesite(localStorage.getItem('site'))
    }, [])

    useEffect(() => {
        if (isValid(company_id) && isValid(choosesite)) {
            getPricingTiers()
        }
    }, [company_id, choosesite, random])

    /* real-time updates: any create/update/delete of a pricing tier refreshes the list for everyone */
    useEffect(() => {
        const clientId = localStorage.getItem('client_id')

        const channelCreated = pusher.subscribe(`${clientId}-quotepricingtier-created`)
        const channelUpdated = pusher.subscribe(`${clientId}-quotepricingtier-updated`)
        const channelDeleted = pusher.subscribe(`${clientId}-quotepricingtier-deleted`)

        channelCreated.bind(`${clientId}-quotepricingtier-created-event`, data => {
            setRandom(Math.random())
        })

        channelUpdated.bind(`${clientId}-quotepricingtier-updated-event`, data => {
            setRandom(Math.random())
        })

        channelDeleted.bind(`${clientId}-quotepricingtier-deleted-event`, data => {
            setRandom(Math.random())
        })

        return (() => {
            pusher.unsubscribe(`${clientId}-quotepricingtier-created`)
            pusher.unsubscribe(`${clientId}-quotepricingtier-updated`)
            pusher.unsubscribe(`${clientId}-quotepricingtier-deleted`)
        })
    }, [])

    const getPricingTiers = async () => {
        setIsLoading(true)

        const url = buildUrl(
            '/api/quote-pricing-tiers',
            {
                with: ['customer', 'item'],
                where: {
                    'company_id': company_id,
                    'site_id': choosesite
                }
            }
        )

        await axios
            .get(url, config)
            .then(res => {
                setTiers(res.data?.data ?? [])
            })
            .catch((err) => console.error(err))
            .finally(() => setIsLoading(false))
    }

    const deleteTier = async (id) => {
        const isConfirm = await Swal.fire({
            title: t('title_delete') + t('del_pricing_tier') + "?",
            text: t('text_delete'),
            icon: 'warning',
            customClass: 'error',
            showCloseButton: true,
            iconColor: '#FF0000',
            reverseButtons: true,
            showCancelButton: true,
            confirmButtonText: t('confirmButtonText_delete'),
            cancelButtonText: t('cancel')
        }).then((result) => result.isConfirmed)

        if (!isConfirm) {
            return
        }

        await axios.delete(`/api/quote-pricing-tiers/${id}`, config).then(({ data }) => {
            Swal.fire({
                icon: "success",
                customClass: 'success',
                showCloseButton: true,
                iconColor: '#00B78E',
                text: data?.success?.message
            })
            getPricingTiers()
        }).catch(({ response }) => {
            Swal.fire({
                text: response?.data?.message,
                icon: "error",
                customClass: 'error',
                showCloseButton: true,
                iconColor: '#FF0000'
            })
        })
    }

    const columns = [
        {
            field: 'customer_name',
            headerName: t('customer'),
            flex: 1.3,
            minWidth: 140,
            valueGetter: ({ row }) => row.customer_name ?? ''
        },
        {
            field: 'item_no',
            headerName: t('item'),
            flex: 1.6,
            minWidth: 160,
            valueGetter: ({ row }) => row.item_no ? `${row.item_no}` : ''
        },
        {
            field: 'min_quantity',
            headerName: t('minimum_quantity'),
            flex: 1,
            minWidth: 110,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'max_quantity',
            headerName: t('maximum_quantity'),
            flex: 1,
            minWidth: 130,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: ({ value }) => isValid(value) ? value : t('no_upper_limit')
        },
        {
            field: 'unit_price',
            headerName: t('unit_price'),
            flex: 1,
            minWidth: 110,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'effective_from',
            headerName: t('effective_from'),
            flex: 1,
            minWidth: 120,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: ({ value }) => isValid(value) ? dayjs(value).format("YYYY-MM-DD") : ''
        },
        {
            field: 'effective_to',
            headerName: t('effective_to'),
            flex: 1,
            minWidth: 120,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: ({ value }) => isValid(value) ? dayjs(value).format("YYYY-MM-DD") : ''
        },
        {
            field: 'actions',
            headerName: t('actions'),
            sortable: false,
            flex: 0.8,
            minWidth: 90,
            cellClassName: 'padding-0',
            renderCell: (params) => (
                <div className='flex justify-between'>
                    <Tooltip disableInteractive title={t('view')} placement='bottom'>
                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => navigate(`${params.row.id}/view`)}><i className="fa-solid fa-eye"></i></span>
                        </div>
                    </Tooltip>
                    <Tooltip disableInteractive title={t('update')} placement='bottom'>
                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => navigate(`${params.row.id}`)}><i className="fa-solid fa-pencil"></i></span>
                        </div>
                    </Tooltip>
                    <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => deleteTier(params.row.id)}><i className="fa-solid fa-trash"></i></span>
                        </div>
                    </Tooltip>
                </div>
            )
        }
    ]

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <AppLayout>
                <div className='p-6 flex w-full items-start justify-between gap-5 max-[1830px]:flex-col'>
                    <div className='w-full bg-white rounded-md'>
                        <div className='p-5 flex justify-between items-center border-b border-[#EEEFF2]'>
                            <div className='flex gap-2 items-center'>
                                <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('volume_tiered_pricing')}</p>
                            </div>
                            <div className='flex gap-5 items-center'>
                                {['master_admin', 'office_manager'].includes(user?.role) ? (
                                    <Tooltip disableInteractive title={t('add_pricing_tier')} placement='bottom'>
                                        <div>
                                            <AddButton onClick={() => navigate('create')}><i className="fa-solid fa-plus text-[#333333]"></i></AddButton>
                                        </div>
                                    </Tooltip>
                                ) : (
                                    ''
                                )}
                            </div>
                        </div>
                        <div>
                            <TableShared items={tiers} columns={columns} />
                        </div>
                    </div>
                </div>
            </AppLayout>
        </>
    )
}

export default QuotePricingTiers
